import React, { useEffect, useMemo, useState } from 'react';
import { NotebookPen, Loader2, Plus, Pencil, Trash2, TrendingUp, TrendingDown, Percent, Wallet, Save, ShieldCheck, FileSpreadsheet, FileText } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import {
  fetchJournals, fetchJournalSummary, createJournal, updateJournal, deleteJournal,
  addJournalFile, fmtMoney, fetchInstruments, fetchJournalSettings, saveJournalSettings,
  downloadJournalXlsx, downloadJournalPdf, JournalPayload, InstrumentPreset,
} from '../../features/journal/api/journal';
import { ImageUpload } from '../../components/common/ImageUpload';
import type { Journal, JournalSummary } from '../../types';
import { StatCard } from '../../components/common/StatCard';
import { JOURNAL } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

const KILLZONES = ['Asia', 'London', 'New York', 'Overlap', 'Other'];
const BIASES = ['Bullish', 'Bearish', 'Neutral'];
const TRIGGERS = ['Rejection', 'Engulfing', 'False Break', 'Break & Retest', 'Order Block', 'Other'];
const EMOTIONS = ['Tenang', 'Disiplin', 'FOMO', 'Serakah', 'Takut', 'Ragu', 'Balas Dendam'];
const PERIODS: { key: string; label: string }[] = [
  { key: 'all', label: 'Semua' }, { key: 'day', label: 'Harian' }, { key: 'week', label: 'Mingguan' },
  { key: 'month', label: 'Bulanan' }, { key: 'year', label: 'Tahunan' },
];

interface FormState {
  instrument: string; trade_side: 'buy' | 'sell'; trade_date: string; time_wib: string;
  killzone: string; bias_d1: string; trigger: string; killer_setup: string;
  entry_price: string; stop_loss: string; take_profit: string; lot: string; exit_price: string;
  follows_rules: string; emotion: string; notes: string; screenshot_url: string;
}
const empty: FormState = {
  instrument: 'XAUUSD', trade_side: 'buy', trade_date: new Date().toISOString().slice(0, 10), time_wib: '',
  killzone: '', bias_d1: '', trigger: '', killer_setup: '0', entry_price: '', stop_loss: '',
  take_profit: '', lot: '', exit_price: '', follows_rules: '', emotion: '', notes: '', screenshot_url: '',
};
const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));

const WL_CLS: Record<string, string> = {
  WIN: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  LOSS: 'text-red-400 bg-red-400/10 border-red-400/20',
  BE: 'text-white/60 bg-white/5 border-white/10',
};
const n2 = (v?: number | null) => (v == null ? '—' : v.toLocaleString('en-US', { maximumFractionDigits: 2 }));

export default function MemberJournalPage() {
  const [journals, setJournals] = useState<Journal[]>([]);
  const [stats, setStats] = useState<JournalSummary | null>(null);
  const [instruments, setInstruments] = useState<InstrumentPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<Journal | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState('month');
  const [capital, setCapital] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [exporting, setExporting] = useState('');

  // Prefill dari Kalkulator Lot ("Pakai di Jurnal")
  useEffect(() => {
    const raw = localStorage.getItem('cuan_journal_prefill');
    if (!raw) return;
    localStorage.removeItem('cuan_journal_prefill');
    try {
      const p = JSON.parse(raw);
      setEditing(null);
      setForm({
        ...empty,
        trade_date: new Date().toISOString().slice(0, 10),
        instrument: p.instrument ?? 'XAUUSD',
        trade_side: p.trade_side ?? 'buy',
        entry_price: p.entry_price != null ? String(p.entry_price) : '',
        stop_loss: p.stop_loss != null ? String(p.stop_loss) : '',
        take_profit: p.take_profit != null ? String(p.take_profit) : '',
        lot: p.lot != null ? String(p.lot) : '',
      });
      setDialog(true);
      toast.success('Data dari Kalkulator Lot dimuat ke form');
    } catch { /* ignore */ }
  }, []);

  const doExport = async (kind: 'xlsx' | 'pdf') => {
    setExporting(kind);
    try {
      const blob = kind === 'xlsx' ? await downloadJournalXlsx() : await downloadJournalPdf();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jurnal-trading-cuantrader.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Jurnal diunduh (${kind.toUpperCase()})`);
    } catch (e: any) {
      toast.error('Gagal mengunduh', { description: e.response?.data?.detail || e.message });
    } finally { setExporting(''); }
  };

  const loadStats = (p: string) => fetchJournalSummary(p).then(setStats);

  const load = () => {
    setLoading(true);
    Promise.all([fetchJournals(), fetchJournalSummary(period), fetchInstruments(), fetchJournalSettings()])
      .then(([j, s, instr, cfg]) => {
        setJournals(j); setStats(s); setInstruments(instr);
        setCapital(cfg.initial_capital ? String(cfg.initial_capital) : '');
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { if (!loading) loadStats(period); }, [period]);

  const preset = useMemo(
    () => instruments.find((i) => i.symbol === form.instrument) || instruments.find((i) => i.symbol === 'XAUUSD'),
    [instruments, form.instrument],
  );

  // live preview of computed values in the form
  const preview = useMemo(() => {
    const e = num(form.entry_price), x = num(form.exit_price), sl = num(form.stop_loss), lot = num(form.lot);
    const ps = preset?.pip_size ?? 0.1, pv = preset?.pip_value ?? 10;
    let pips: number | null = null, amt: number | null = null, risk: number | null = null, r: number | null = null;
    if (e != null && x != null && ps) { pips = Math.round(((form.trade_side === 'buy' ? x - e : e - x) / ps) * 10) / 10; if (lot != null) amt = Math.round(pips * lot * pv * 100) / 100; }
    if (e != null && sl != null && lot != null && ps) risk = Math.round((Math.abs(e - sl) / ps) * lot * pv * 100) / 100;
    if (amt != null && risk) r = Math.round((amt / risk) * 100) / 100;
    return { pips, amt, risk, r };
  }, [form, preset]);

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (j: Journal) => {
    setEditing(j);
    setForm({
      instrument: j.instrument || 'XAUUSD', trade_side: j.trade_side, trade_date: j.trade_date || '',
      time_wib: j.time_wib || '', killzone: j.killzone || '', bias_d1: j.bias_d1 || '', trigger: j.trigger || '',
      killer_setup: String(j.killer_setup ?? 0), entry_price: j.entry_price?.toString() ?? '',
      stop_loss: j.stop_loss?.toString() ?? '', take_profit: j.take_profit?.toString() ?? '',
      lot: j.lot?.toString() ?? '', exit_price: j.exit_price?.toString() ?? '',
      follows_rules: j.follows_rules == null ? '' : j.follows_rules ? 'yes' : 'no',
      emotion: j.emotion || '', notes: j.notes || '', screenshot_url: '',
    });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    const payload: JournalPayload = {
      instrument: form.instrument, trade_side: form.trade_side, trade_date: form.trade_date,
      time_wib: form.time_wib, killzone: form.killzone, bias_d1: form.bias_d1, trigger: form.trigger,
      killer_setup: Number(form.killer_setup || 0), entry_price: num(form.entry_price), stop_loss: num(form.stop_loss),
      take_profit: num(form.take_profit), lot: num(form.lot), exit_price: num(form.exit_price),
      follows_rules: form.follows_rules === '' ? null : form.follows_rules === 'yes',
      emotion: form.emotion, notes: form.notes,
    };
    try {
      if (editing) await updateJournal(editing.id, payload);
      else {
        const created = await createJournal(payload);
        if (form.screenshot_url) { try { await addJournalFile(created.id, form.screenshot_url); } catch { /* non-blocking */ } }
      }
      toast.success('Jurnal tersimpan');
      setDialog(false); load();
    } catch (e: any) {
      toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const remove = async (j: Journal) => {
    if (!window.confirm('Hapus catatan trade ini?')) return;
    try { await deleteJournal(j.id); toast.success('Jurnal dihapus'); load(); }
    catch (e: any) { toast.error('Gagal menghapus', { description: e.response?.data?.detail || e.message }); }
  };

  const saveCapital = async () => {
    setSavingCap(true);
    try { await saveJournalSettings(Number(capital || 0)); toast.success('Modal awal disimpan'); loadStats(period); }
    catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSavingCap(false); }
  };

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Trading Journal">
      <div className="max-w-full" data-testid={JOURNAL.memberPage}>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <NotebookPen className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Jurnal ICC</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Trading Journal</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="journal-export-xlsx" onClick={() => doExport('xlsx')} disabled={exporting !== ''} className="bg-white/5 border border-white/10 text-white/80 hover:text-white hover:bg-white/10 font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50">
              {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-400" />} Excel
            </button>
            <button data-testid="journal-export-pdf" onClick={() => doExport('pdf')} disabled={exporting !== ''} className="bg-white/5 border border-white/10 text-white/80 hover:text-white hover:bg-white/10 font-medium px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50">
              {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 text-red-400" />} PDF
            </button>
            <button data-testid={JOURNAL.submit + '-open'} onClick={openNew} className="bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 flex items-center gap-2">
              <Plus className="w-4 h-4" strokeWidth={2} /> Catat Trade
            </button>
          </div>
        </div>

        {/* Modal awal + periode */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div className="flex items-end gap-2">
            <div>
              <label className={labelClass}>Modal awal jurnal ($)</label>
              <input data-testid="journal-initial-capital" type="number" step="any" className={inputClass + ' w-40'} value={capital} onChange={(e) => setCapital(e.target.value)} placeholder="1000" />
            </div>
            <button data-testid="journal-save-capital" onClick={saveCapital} disabled={savingCap} className="bg-white/5 border border-white/10 text-white/80 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm disabled:opacity-50">
              {savingCap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
            </button>
          </div>
          <div className="flex gap-1 bg-white/[0.03] border border-white/10 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button key={p.key} data-testid={`journal-period-${p.key}`} onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${period === p.key ? 'bg-cyan-400 text-[#050914] font-medium' : 'text-white/60 hover:text-white'}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard icon={NotebookPen} label="Total Trades" testId={JOURNAL.summaryTotal} accent>
            <p className="text-2xl font-light text-white">{stats?.total_trades ?? 0}</p>
          </StatCard>
          <StatCard icon={Percent} label="Win Rate" testId={JOURNAL.summaryWinrate}>
            <p className="text-2xl font-light text-white">{stats?.win_rate ?? 0}%</p>
            <p className="text-xs text-white/40 mt-1">{stats?.wins ?? 0}W / {stats?.losses ?? 0}L</p>
          </StatCard>
          <StatCard icon={stats && stats.total_profit_loss >= 0 ? TrendingUp : TrendingDown} label="Total P/L ($)" testId={JOURNAL.summaryPl}>
            <p className={`text-2xl font-light ${stats && stats.total_profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtMoney(stats?.total_profit_loss)}</p>
          </StatCard>
          <StatCard icon={Wallet} label="Equity Sekarang ($)" testId="journal-summary-equity">
            <p className="text-2xl font-light text-white">{n2(stats?.equity_now)}</p>
          </StatCard>
          <StatCard icon={TrendingUp} label="Rata-rata R" testId="journal-summary-avgr">
            <p className="text-2xl font-light text-white">{stats?.avg_r ?? 0}</p>
          </StatCard>
        </div>

        {/* Disiplin */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-xs text-white/50">Win rate — IKUT aturan ({stats?.follow_count ?? 0} trade)</p>
              <p className="text-lg text-emerald-400">{stats?.win_rate_follow ?? 0}%</p>
            </div>
          </div>
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-xs text-white/50">Win rate — MELANGGAR aturan ({stats?.violate_count ?? 0} trade)</p>
              <p className="text-lg text-red-400">{stats?.win_rate_violate ?? 0}%</p>
            </div>
          </div>
        </div>

        {/* Equity curve */}
        {stats?.equity_curve && stats.equity_curve.length > 1 && (
          <div className="mb-8 bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="journal-equity-curve">
            <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Kurva Pertumbuhan Equity</h3>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={stats.equity_curve} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} tickLine={false} axisLine={false} width={60} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: '#0b1324', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 12 }} formatter={(v: any) => [`$${Number(v).toLocaleString('en-US')}`, 'Equity']} />
                <Area type="monotone" dataKey="equity" stroke="#22d3ee" strokeWidth={2} fill="url(#eqGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Buckets per periode */}
        {stats?.buckets && stats.buckets.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Ringkasan per {PERIODS.find((p) => p.key === period)?.label}</h3>
            <div className="overflow-x-auto border border-white/10 rounded-xl">
              <table className="w-full text-sm" data-testid="journal-buckets-table">
                <thead><tr className="bg-[#0b1324] text-white/60 text-xs">
                  <th className="text-left px-4 py-2.5">Periode</th><th className="px-3 py-2.5">Trade</th><th className="px-3 py-2.5">W/L</th><th className="px-3 py-2.5">Win Rate</th><th className="px-3 py-2.5">Avg R</th><th className="text-right px-4 py-2.5">P/L ($)</th>
                </tr></thead>
                <tbody>
                  {stats.buckets.map((b) => (
                    <tr key={b.label} className="border-t border-white/5">
                      <td className="px-4 py-2.5 text-white">{b.label}</td>
                      <td className="px-3 py-2.5 text-center text-white/70">{b.total_trades}</td>
                      <td className="px-3 py-2.5 text-center text-white/70">{b.wins}/{b.losses}</td>
                      <td className="px-3 py-2.5 text-center text-white/70">{b.win_rate}%</td>
                      <td className="px-3 py-2.5 text-center text-white/70">{b.avg_r}</td>
                      <td className={`px-4 py-2.5 text-right font-medium ${b.total_profit_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtMoney(b.total_profit_loss)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* History */}
        <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Riwayat Trade</h3>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : journals.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada catatan trade. Klik "Catat Trade" untuk mulai.</p>
        ) : (
          <div className="overflow-x-auto border border-white/10 rounded-xl">
            <table className="w-full text-xs whitespace-nowrap" data-testid="journal-table">
              <thead><tr className="bg-[#0b1324] text-white/50">
                {['Tanggal', 'Jam', 'Killzone', 'Instr', 'Arah', 'Bias', 'Trigger', 'KS', 'Entry', 'SL', 'TP', 'Lot', 'Exit', 'Pips', 'Hasil $', 'Risk $', 'R', 'W/L', 'Aturan', 'Emosi', ''].map((h, i) => (
                  <th key={i} className={`px-2.5 py-2.5 ${['Tanggal', 'Killzone', 'Trigger', 'Emosi'].includes(h) ? 'text-left' : 'text-center'}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {journals.map((j) => (
                  <tr key={j.id} data-testid={JOURNAL.row} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-2.5 py-2 text-white/80">{j.trade_date || '—'}</td>
                    <td className="px-2.5 py-2 text-center text-white/60">{j.time_wib || '—'}</td>
                    <td className="px-2.5 py-2 text-white/60">{j.killzone || '—'}</td>
                    <td className="px-2.5 py-2 text-center text-white/80">{j.instrument}</td>
                    <td className="px-2.5 py-2 text-center"><span className={j.trade_side === 'buy' ? 'text-sky-300' : 'text-orange-300'}>{j.trade_side.toUpperCase()}</span></td>
                    <td className="px-2.5 py-2 text-center text-white/60">{j.bias_d1 || '—'}</td>
                    <td className="px-2.5 py-2 text-white/60">{j.trigger || '—'}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{j.killer_setup ?? 0}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{n2(j.entry_price)}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{n2(j.stop_loss)}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{n2(j.take_profit)}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{n2(j.lot)}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{n2(j.exit_price)}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{j.result_pips == null ? '—' : j.result_pips}</td>
                    <td className={`px-2.5 py-2 text-center font-medium ${(j.result_amount ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{j.result_amount == null ? '—' : fmtMoney(j.result_amount)}</td>
                    <td className="px-2.5 py-2 text-center text-white/60">{n2(j.risk_amount)}</td>
                    <td className="px-2.5 py-2 text-center text-white/70">{j.r_multiple == null ? '—' : j.r_multiple}</td>
                    <td className="px-2.5 py-2 text-center">{j.win_loss ? <span className={`px-2 py-0.5 rounded-full border ${WL_CLS[j.win_loss]}`}>{j.win_loss}</span> : '—'}</td>
                    <td className="px-2.5 py-2 text-center">{j.follows_rules == null ? '—' : j.follows_rules ? <span className="text-emerald-400">Ya</span> : <span className="text-red-400">Tidak</span>}</td>
                    <td className="px-2.5 py-2 text-white/60">{j.emotion || '—'}</td>
                    <td className="px-2.5 py-2 text-center">
                      <div className="flex gap-1">
                        <button data-testid={JOURNAL.editBtn} onClick={() => openEdit(j)} className="p-1.5 rounded text-white/60 hover:text-cyan-400 hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={JOURNAL.deleteBtn} onClick={() => remove(j)} className="p-1.5 rounded text-white/60 hover:text-red-400 hover:bg-white/5"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Trade' : 'Catat Trade Baru'}</DialogTitle>
            <DialogDescription className="text-white/50">Kolom kuning diisi sendiri; pips, hasil $, risiko, R, W/L dihitung otomatis.</DialogDescription>
          </DialogHeader>
          <form data-testid={JOURNAL.form} onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className={labelClass}>Tanggal</label><input type="date" data-testid="journal-date" className={inputClass} value={form.trade_date} onChange={(e) => setForm({ ...form, trade_date: e.target.value })} /></div>
              <div><label className={labelClass}>Jam WIB</label><input type="time" className={inputClass} value={form.time_wib} onChange={(e) => setForm({ ...form, time_wib: e.target.value })} /></div>
              <div><label className={labelClass}>Killzone</label><select className={inputClass} value={form.killzone} onChange={(e) => setForm({ ...form, killzone: e.target.value })}><option value="" className="bg-[#090D1A]">—</option>{KILLZONES.map((k) => <option key={k} value={k} className="bg-[#090D1A]">{k}</option>)}</select></div>
              <div><label className={labelClass}>Pair / Instrumen</label><select data-testid="journal-instrument" className={inputClass} value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })}>{instruments.map((i) => <option key={i.symbol} value={i.symbol} className="bg-[#090D1A]">{i.symbol}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className={labelClass}>Arah</label><select className={inputClass} value={form.trade_side} onChange={(e) => setForm({ ...form, trade_side: e.target.value as any })}><option value="buy" className="bg-[#090D1A]">BUY</option><option value="sell" className="bg-[#090D1A]">SELL</option></select></div>
              <div><label className={labelClass}>Bias D1</label><select className={inputClass} value={form.bias_d1} onChange={(e) => setForm({ ...form, bias_d1: e.target.value })}><option value="" className="bg-[#090D1A]">—</option>{BIASES.map((b) => <option key={b} value={b} className="bg-[#090D1A]">{b}</option>)}</select></div>
              <div><label className={labelClass}>Trigger</label><select className={inputClass} value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })}><option value="" className="bg-[#090D1A]">—</option>{TRIGGERS.map((t) => <option key={t} value={t} className="bg-[#090D1A]">{t}</option>)}</select></div>
              <div><label className={labelClass}>Killer Setup (0–5)</label><select className={inputClass} value={form.killer_setup} onChange={(e) => setForm({ ...form, killer_setup: e.target.value })}>{[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n} className="bg-[#090D1A]">{n}</option>)}</select></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div><label className={labelClass}>Entry</label><input type="number" step="any" data-testid="journal-entry" className={inputClass} value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} /></div>
              <div><label className={labelClass}>SL</label><input type="number" step="any" className={inputClass} value={form.stop_loss} onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} /></div>
              <div><label className={labelClass}>TP</label><input type="number" step="any" className={inputClass} value={form.take_profit} onChange={(e) => setForm({ ...form, take_profit: e.target.value })} /></div>
              <div><label className={labelClass}>Lot</label><input type="number" step="any" data-testid="journal-lot" className={inputClass} value={form.lot} onChange={(e) => setForm({ ...form, lot: e.target.value })} /></div>
              <div><label className={labelClass}>Harga Exit</label><input type="number" step="any" data-testid="journal-exit" className={inputClass} value={form.exit_price} onChange={(e) => setForm({ ...form, exit_price: e.target.value })} /></div>
            </div>

            {/* Preview otomatis */}
            <div className="grid grid-cols-4 gap-2 bg-[#050914] border border-cyan-400/20 rounded-lg p-3 text-center" data-testid="journal-preview">
              <div><p className="text-[10px] uppercase text-white/40">Pips</p><p className="text-sm text-white">{preview.pips ?? '—'}</p></div>
              <div><p className="text-[10px] uppercase text-white/40">Hasil $</p><p className={`text-sm ${(preview.amt ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{preview.amt == null ? '—' : fmtMoney(preview.amt)}</p></div>
              <div><p className="text-[10px] uppercase text-white/40">Risiko $</p><p className="text-sm text-white">{preview.risk ?? '—'}</p></div>
              <div><p className="text-[10px] uppercase text-white/40">R</p><p className="text-sm text-white">{preview.r ?? '—'}</p></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelClass}>Ikut aturan?</label><select data-testid="journal-rules" className={inputClass} value={form.follows_rules} onChange={(e) => setForm({ ...form, follows_rules: e.target.value })}><option value="" className="bg-[#090D1A]">—</option><option value="yes" className="bg-[#090D1A]">Ya</option><option value="no" className="bg-[#090D1A]">Tidak</option></select></div>
              <div><label className={labelClass}>Emosi</label><select className={inputClass} value={form.emotion} onChange={(e) => setForm({ ...form, emotion: e.target.value })}><option value="" className="bg-[#090D1A]">—</option>{EMOTIONS.map((em) => <option key={em} value={em} className="bg-[#090D1A]">{em}</option>)}</select></div>
            </div>
            <div><label className={labelClass}>Catatan / pelajaran</label><textarea className={inputClass} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            {!editing && (
              <div><label className={labelClass}>Link Screenshot (opsional)</label><ImageUpload value={form.screenshot_url} onUploaded={(url) => setForm({ ...form, screenshot_url: url })} label="Unggah Screenshot" testId="journal-screenshot-upload" /></div>
            )}
            <DialogFooter>
              <button type="submit" data-testid={JOURNAL.submit} disabled={saving} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
